import React, { useEffect, useMemo, useState } from 'react';
import type { SvgIconComponent } from '@mui/icons-material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useTranslation } from 'react-i18next';
import { ESG_CONTENT_SHAPE } from '../../constants/colors';
import { ESG_ICONS } from '../../constants/esgIcons';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import {
  GhgScope,
  ScopeEmissionsSummary,
  UtilityActivityLine,
  UtilityActivityType,
  UtilityCategory,
} from '../../types/utility-emission.type';
import { listUtilityCatalog } from '../../services/utility-emission.service';
import KpiFigure from '../metrics/KpiFigure';
import MetricStrip from '../metrics/MetricStrip';

enum UtilityBreakdownSortField {
  UTILITY = 'utility',
  ACTIVITY = 'activity',
  SCOPE = 'scope',
  FACTOR_VERSION = 'factor_version',
  EMISSIONS = 'emissions',
}

enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

enum ScopeFilter {
  ALL = 'all',
  SCOPE_1 = 'scope_1',
  SCOPE_2 = 'scope_2',
  NOT_SCOPED = 'not_scoped',
}

const utilityIcons: Record<UtilityCategory, SvgIconComponent> = {
  [UtilityCategory.ELECTRICITY]: ESG_ICONS.electricity,
  [UtilityCategory.GAS]: ESG_ICONS.gas,
  [UtilityCategory.OIL]: ESG_ICONS.oil,
  [UtilityCategory.WATER]: ESG_ICONS.water,
  [UtilityCategory.OTHER]: ESG_ICONS.materials,
};

const monoCaptionSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: '0.75rem',
  color: 'text.secondary',
} as const;

interface ScopeEmissionsPanelProps {
  summary: ScopeEmissionsSummary;
  showBreakdown?: boolean;
  showScope1?: boolean;
  showScope2?: boolean;
  /** Context line under the scope figures (e.g. reporting month). */
  context?: React.ReactNode;
}

function compareLines(
  left: UtilityActivityLine,
  right: UtilityActivityLine,
  field: UtilityBreakdownSortField
): number {
  switch (field) {
    case UtilityBreakdownSortField.ACTIVITY:
      return left.consumption - right.consumption;
    case UtilityBreakdownSortField.SCOPE:
      return (left.ghg_scope ?? Number.MAX_SAFE_INTEGER) -
        (right.ghg_scope ?? Number.MAX_SAFE_INTEGER);
    case UtilityBreakdownSortField.FACTOR_VERSION:
      return (left.factor_version_label ?? '').localeCompare(
        right.factor_version_label ?? ''
      );
    case UtilityBreakdownSortField.EMISSIONS:
      return (left.emissions_tco2e ?? Number.MAX_SAFE_INTEGER) -
        (right.emissions_tco2e ?? Number.MAX_SAFE_INTEGER);
    case UtilityBreakdownSortField.UTILITY:
    default:
      return left.label.localeCompare(right.label);
  }
}

function matchesScope(line: UtilityActivityLine, filter: ScopeFilter): boolean {
  switch (filter) {
    case ScopeFilter.SCOPE_1:
      return line.ghg_scope === GhgScope.SCOPE_1;
    case ScopeFilter.SCOPE_2:
      return line.ghg_scope === GhgScope.SCOPE_2;
    case ScopeFilter.NOT_SCOPED:
      return line.ghg_scope === null;
    case ScopeFilter.ALL:
    default:
      return true;
  }
}

const UtilityBreakdownTable: React.FC<{
  lines: UtilityActivityLine[];
}> = ({ lines }) => {
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const [catalog, setCatalog] = useState<UtilityActivityType[]>([]);
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>(ScopeFilter.ALL);
  const [sortField, setSortField] = useState<UtilityBreakdownSortField>(
    UtilityBreakdownSortField.UTILITY
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.ASC
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    let active = true;
    void listUtilityCatalog()
      .then((utilityTypes) => {
        if (active) setCatalog(utilityTypes);
      })
      .catch(() => {
        if (active) setCatalog([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setPage(0);
  }, [lines]);

  const completeLines = useMemo(() => {
    const existingKeys = new Set(lines.map((line) => line.utility_type_key));
    const notApplicableLines: UtilityActivityLine[] = catalog
      .filter(
        (utilityType) =>
          utilityType.ghg_scope === null && !existingKeys.has(utilityType.key)
      )
      .map((utilityType) => ({
        utility_type_key: utilityType.key,
        label: utilityType.label,
        category: utilityType.category,
        canonical_unit: utilityType.canonical_unit,
        ghg_scope: null,
        consumption: 0,
        amount_sar: 0,
        factor_version_label: null,
        emissions_tco2e: 0,
        missing_factor: false,
      }));
    return [...lines, ...notApplicableLines];
  }, [catalog, lines]);

  const filteredLines = useMemo(() => {
    const query = search.trim().toLowerCase();
    return completeLines
      .filter((line) => {
        if (!matchesScope(line, scopeFilter)) return false;
        if (!query) return true;
        return [
          line.label,
          line.utility_type_key,
          line.category,
          line.canonical_unit,
          line.factor_version_label ?? '',
        ].some((value) => value.toLowerCase().includes(query));
      })
      .sort((left, right) => {
        const result = compareLines(left, right, sortField);
        return sortDirection === SortDirection.ASC ? result : -result;
      });
  }, [completeLines, scopeFilter, search, sortDirection, sortField]);

  const visibleLines = filteredLines.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSort = (field: UtilityBreakdownSortField) => {
    if (sortField === field) {
      setSortDirection((current) =>
        current === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
      );
    } else {
      setSortField(field);
      setSortDirection(SortDirection.ASC);
    }
    setPage(0);
  };

  const handleScopeFilter = (event: SelectChangeEvent<ScopeFilter>) => {
    setScopeFilter(event.target.value as ScopeFilter);
    setPage(0);
  };

  const sortableHeader = (
    label: string,
    field: UtilityBreakdownSortField,
    align: 'left' | 'right' = 'left'
  ) => (
    <TableCell align={align} sortDirection={sortField === field ? sortDirection : false}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDirection : SortDirection.ASC}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: `${ESG_CONTENT_SHAPE.md}px` }}>
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 2,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="subtitle2" component="h3" sx={{ m: 0 }}>
          {t('dashboard.scopeWidgets.breakdown')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, flex: '1 1 360px', justifyContent: 'flex-end' }}>
          <TextField
            size="small"
            label={t('dashboard.inventory.searchUtilities')}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            sx={{ flex: '1 1 220px', maxWidth: 320 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="scope-filter-label">{t('dashboard.inventory.scope')}</InputLabel>
            <Select
              labelId="scope-filter-label"
              value={scopeFilter}
              label={t('dashboard.inventory.scope')}
              onChange={handleScopeFilter}
            >
              <MenuItem value={ScopeFilter.ALL}>{t('dashboard.inventory.allScopes')}</MenuItem>
              <MenuItem value={ScopeFilter.SCOPE_1}>{t('dashboard.inventory.scopeN', { n: 1 })}</MenuItem>
              <MenuItem value={ScopeFilter.SCOPE_2}>{t('dashboard.inventory.scopeN', { n: 2 })}</MenuItem>
              <MenuItem value={ScopeFilter.NOT_SCOPED}>{t('dashboard.inventory.notApplicable')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              {sortableHeader(t('dashboard.inventory.columns.utility'), UtilityBreakdownSortField.UTILITY)}
              {sortableHeader(t('dashboard.inventory.columns.activity'), UtilityBreakdownSortField.ACTIVITY, 'right')}
              {sortableHeader(t('dashboard.inventory.columns.scope'), UtilityBreakdownSortField.SCOPE)}
              {sortableHeader(t('dashboard.inventory.columns.factor'), UtilityBreakdownSortField.FACTOR_VERSION)}
              {sortableHeader(t('dashboard.inventory.columns.emissions'), UtilityBreakdownSortField.EMISSIONS, 'right')}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleLines.map((line) => {
              const Icon = utilityIcons[line.category] ?? ESG_ICONS.materials;
              return (
                <TableRow hover key={line.utility_type_key}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Icon sx={{ fontSize: 18, color: 'text.secondary' }} aria-hidden />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {line.label}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <bdi>{fmt.number(line.consumption)}</bdi>
                    <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                      {line.canonical_unit}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {line.ghg_scope
                      ? t('dashboard.inventory.scopeN', { n: line.ghg_scope })
                      : t('dashboard.inventory.notApplicable')}
                  </TableCell>
                  <TableCell>
                    {line.factor_version_label ? (
                      <Box component="span" sx={monoCaptionSx}>
                        {line.factor_version_label}
                      </Box>
                    ) : line.missing_factor ? (
                      <Chip size="small" variant="outlined" color="warning" label={t('dashboard.inventory.factorMissing')} />
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        {t('dashboard.inventory.notApplicable')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {line.emissions_tco2e === null ? (
                      <Typography variant="caption" color="warning.main">
                        {t('dashboard.inventory.incomplete')}
                      </Typography>
                    ) : (
                      <bdi>{fmt.metric('tco2e', line.emissions_tco2e).text}</bdi>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {visibleLines.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" variant="body2">
                    {t('dashboard.inventory.noMatches')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <TablePagination
        component="div"
        count={filteredLines.length}
        page={page}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25]}
        labelRowsPerPage={t('common.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) =>
          t('common.rangeOf', { from: fmt.number(from), to: fmt.number(to), count: fmt.number(count) })
        }
      />
    </Paper>
  );
};

/**
 * GHG inventory (Scope 1 & 2, location-based) — rendered as a hairline figure
 * strip, never mixed with avoided emissions.
 */
const ScopeEmissionsPanel: React.FC<ScopeEmissionsPanelProps> = ({
  summary,
  showBreakdown = true,
  showScope1 = true,
  showScope2 = true,
  context,
}) => {
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const showCards = showScope1 || showScope2;
  if (!showCards && !showBreakdown) return null;

  const scopeFigure = (n: 1 | 2, value: number | null, complete: boolean) => {
    const figure = fmt.metric('tco2e', value);
    return (
      <KpiFigure
        label={t(n === 1 ? 'dashboard.inventory.scope1' : 'dashboard.inventory.scope2')}
        icon={n === 1 ? ESG_ICONS.scope1 : ESG_ICONS.scope2}
        value={figure.text}
        unit={figure.unit}
        size="lg"
        context={
          <>
            <Box
              component="span"
              sx={{ color: complete && value !== null ? 'success.main' : 'warning.main', fontWeight: 600 }}
            >
              {complete && value !== null ? t('dashboard.inventory.complete') : t('dashboard.inventory.incomplete')}
            </Box>
            {context ? <> · {context}</> : null}
          </>
        }
      />
    );
  };

  const cells = [
    showScope1 ? <React.Fragment key="s1">{scopeFigure(1, summary.scope_1_tco2e, summary.scope_1_complete)}</React.Fragment> : null,
    showScope2 ? <React.Fragment key="s2">{scopeFigure(2, summary.scope_2_tco2e, summary.scope_2_complete)}</React.Fragment> : null,
  ].filter(Boolean);

  return (
    <Box>
      {summary.missing_factor_utility_keys.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('dashboard.inventory.missingFactors', { keys: summary.missing_factor_utility_keys.join(', ') })}
        </Alert>
      )}
      {showCards && (
        <MetricStrip
          columns={{ xs: 1, sm: Math.min(cells.length, 2), md: cells.length }}
          sx={{ mb: showBreakdown ? 2.5 : 0 }}
        >
          {cells}
        </MetricStrip>
      )}
      {showBreakdown && <UtilityBreakdownTable lines={summary.activity_lines} />}
    </Box>
  );
};

export default ScopeEmissionsPanel;
